import { CATALOGUE, CATEGORIES, catalogueItem, sizeAssumed, type CatalogueItem, type Category } from '@mawkeb/shared/catalogue';
import { ROW_DEFAULTS, seatCount } from '@mawkeb/shared/layout';
import { formatLength, type UnitSystem } from '@mawkeb/shared/units';
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { layCarpet } from '../engine/editing';
import { layoutState, recentItems, useLayout } from '../state/layout';
import { useVenue, venue } from '../state/store';
import { Close, Search } from './icons';
import { viewer } from './Stage';

export const ROWS_ENTRY = 'rows:chair-banquet-taha';
const CARPET_ENTRY = 'action:carpet';

interface Entry {
  id: string;
  name: string;
  category: Category;
  size: string;
  assumed: boolean;
  thumb: string;
}

function sizeText(item: CatalogueItem, units: UnitSystem): string {
  const f = (m: number) => formatLength(m, units);
  const [w, d, h] = item.size;
  if (item.make === 'panel') return `${f(w)} × ${f(h)}`;
  if (item.make === 'carpet') return `${f(w)} × ${f(d)}`;
  if (item.make === 'person') return `${f(item.person?.height ?? h)} tall`;
  return `${f(w)} × ${f(d)}, ${f(h)} high`;
}

function entriesFor(units: UnitSystem, thumbs: Record<string, string>): Entry[] {
  const chair = catalogueItem('chair-banquet-taha')!;
  const rows: Entry = {
    id: ROWS_ENTRY, name: 'Rows of chairs', category: 'seating', assumed: true, thumb: thumbs[chair.id] ?? '',
    size: `${ROW_DEFAULTS.perRow} in a row, ${ROW_DEFAULTS.rows} rows, ${seatCount(ROW_DEFAULTS)} seats`,
  };
  const carpet: Entry = {
    id: CARPET_ENTRY, name: 'Lay the carpet', category: 'carpet', assumed: false, thumb: thumbs['carpet-strip-zahraa'] ?? '',
    size: 'All four strips, from the stage',
  };
  return [rows, carpet, ...CATALOGUE.map((c) => ({ id: c.id, name: c.name, category: c.category, size: sizeText(c, units), assumed: sizeAssumed(c), thumb: thumbs[c.id] ?? '' }))];
}

/** Starts placing an item (a tap or click on the floor puts it there), or lays the carpet at once. */
function choose(id: string) {
  if (id === CARPET_ENTRY) layTheCarpet();
  else {
    const st = layoutState();
    st.setPlacing(st.placing === id ? null : id);
  }
  // on a phone the sheet covers the hall: it closes so the floor can be tapped, or the carpet seen
  if (matchMedia('(pointer: coarse)').matches) venue().set({ library: false });
}

function layTheCarpet() {
  const model = venue().model;
  const st = layoutState();
  if (!model) return;
  const split = st.layout.divider.on;
  const r = layCarpet(st.layout, model, split);
  if (!r.ids.length) return venue().toast('The carpet needs a stage to run from.');
  st.commit(r.layout);
  st.select(r.ids);
  venue().toast(split ? `Laid ${r.ids.length} carpet strips from the stage to the divider.` : `Laid ${r.ids.length} carpet strips from the stage.`);
}

/** With a mouse, an item can be dragged from the panel onto the hall and placed where it is dropped. */
function useDragIn(id: string) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const mouse = useRef(false);
  return {
    mouse,
    onPointerDown(e: ReactPointerEvent) {
      mouse.current = e.pointerType === 'mouse';
      if (!mouse.current || e.button !== 0) return;
      start.current = { x: e.clientX, y: e.clientY };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    onPointerUp(e: ReactPointerEvent) {
      const s = start.current;
      start.current = null;
      if (!s) return;
      if (Math.hypot(e.clientX - s.x, e.clientY - s.y) < 6) return choose(id);
      const canvas = document.querySelector<HTMLCanvasElement>('.stage canvas');
      const under = document.elementFromPoint(e.clientX, e.clientY);
      const v = viewer();
      if (!canvas || !v || !(under === canvas || under?.closest('.overlay'))) return;
      const r = canvas.getBoundingClientRect();
      if (!v.placeAt(id, e.clientX - r.left, e.clientY - r.top)) venue().toast('Drop it on the floor of the hall.');
    },
  };
}

function Item({ e, placing, tile }: { e: Entry; placing: boolean; tile: boolean }) {
  const drag = useDragIn(e.id);
  return (
    <button
      type="button"
      className={tile ? 'tile' : 'item'}
      aria-pressed={placing}
      onClick={() => !drag.mouse.current && choose(e.id)}
      onPointerDown={drag.onPointerDown}
      onPointerUp={drag.onPointerUp}
      onKeyDown={() => (drag.mouse.current = false)}
    >
      <span className="thumb">{e.thumb ? <img src={e.thumb} alt="" draggable={false} /> : null}</span>
      <span className="text">
        <span className="name">{e.name}</span>
        <span className="size">
          {e.size}
          {e.assumed && <span className="tag">size assumed</span>}
        </span>
      </span>
    </button>
  );
}

export function Library() {
  const open = useVenue((s) => s.library);
  const units = useVenue((s) => s.units);
  const thumbs = useVenue((s) => s.thumbs);
  const set = useVenue((s) => s.set);
  const placing = useLayout((s) => s.placing);
  const [chip, setChip] = useState<Category | 'all'>('all');
  const [query, setQuery] = useState('');
  const entries = useMemo(() => entriesFor(units, thumbs), [units, thumbs]);
  if (!open) return null;
  const q = query.trim().toLowerCase();
  const shown = entries.filter((e) => (chip === 'all' || e.category === chip) && (!q || e.name.toLowerCase().includes(q)));
  const tile = matchMedia('(max-width: 760px)').matches;
  const groups: { title: string; list: Entry[] }[] = [];
  if (chip === 'all' && !q) {
    const recents = recentItems().map((id) => entries.find((e) => e.id === id)).filter((e): e is Entry => !!e);
    if (recents.length) groups.push({ title: 'Recently used', list: recents });
    for (const c of CATEGORIES) {
      const list = shown.filter((e) => e.category === c.id);
      if (list.length) groups.push({ title: c.name, list });
    }
  } else {
    groups.push({ title: '', list: shown });
  }
  return (
    <aside className="panel library" aria-label="Props">
      <header>
        <div className="grab only-phone" aria-hidden="true" />
        <div className="search">
          <Search />
          <input type="search" placeholder="Search props" aria-label="Search props" value={query} onChange={(e) => setQuery(e.target.value)} />
          <button type="button" className="icon" aria-label="Close the props" title="Close (Esc)" onClick={() => set({ library: false })}>
            <Close />
          </button>
        </div>
        <div className="chips" role="group" aria-label="Kinds of prop">
          <button type="button" className="chip" aria-pressed={chip === 'all'} onClick={() => setChip('all')}>
            All
          </button>
          {CATEGORIES.map((c) => (
            <button key={c.id} type="button" className="chip" aria-pressed={chip === c.id} onClick={() => setChip(c.id)}>
              {c.name}
            </button>
          ))}
        </div>
      </header>
      <div className={tile ? 'tiles' : 'items'}>
        {groups.map((g) => (
          <section key={g.title || 'all'} aria-label={g.title || 'Props'}>
            {g.title && <h3 className="group">{g.title}</h3>}
            {g.list.map((e) => (
              <Item key={`${g.title}-${e.id}`} e={e} placing={placing === e.id} tile={tile} />
            ))}
          </section>
        ))}
        {!shown.length && <p className="hint empty">Nothing called “{query}”.</p>}
      </div>
    </aside>
  );
}

/** While an item waits to be placed: what to do next, and a way out. */
export function PlacingHint() {
  const placing = useLayout((s) => s.placing);
  const setPlacing = useLayout((s) => s.setPlacing);
  if (!placing) return null;
  const name = placing === ROWS_ENTRY ? 'rows of chairs' : (catalogueItem(placing)?.name ?? 'it').toLowerCase();
  const touch = matchMedia('(pointer: coarse)').matches;
  return (
    <div className="panel placing" role="status">
      <span>{touch ? 'Tap' : 'Click'} the floor to place the {name}</span>
      <button type="button" className="secondary" onClick={() => setPlacing(null)}>
        Cancel
      </button>
    </div>
  );
}
